import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import {
    credentialService, LIGHT_LABEL, STATE_LABEL,
    type CredentialState, type CredentialType, type ExpiringItem, type Light, type PersonView, type TypeInput,
} from '@/services/credentialService'

const inputCls = 'w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-blue-500/50 focus:outline-none'
const btnPrimary = 'rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50'
const btnGhost = 'rounded-xl border border-white/10 px-3 py-1.5 text-xs text-gray-300 hover:bg-white/5'

const errorMessage = (e: unknown, fallback: string) =>
    (e as { response?: { data?: { message?: string; error?: string } } })?.response?.data?.message
    ?? (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? fallback

const LIGHT_CLS: Record<Light, string> = {
    GREEN: 'bg-emerald-500/15 text-emerald-300',
    AMBER: 'bg-amber-500/15 text-amber-300',
    RED: 'bg-rose-500/15 text-rose-300',
}
const STATE_CLS: Record<CredentialState, string> = {
    VALID: 'text-emerald-300', NO_EXPIRY: 'text-emerald-300', EXPIRING: 'text-amber-300', EXPIRED: 'text-rose-300', MISSING: 'text-rose-300',
}

const fmt = (d: string | null) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('es-CL') : '—')

type Tab = 'mine' | 'people' | 'expiring' | 'types'

/** Acreditaciones del personal: semáforo por persona, vencimientos y tipos. */
export default function CredentialsPage() {
    const { user } = useAuth()
    const canManage = !!user?.roles?.some((r) => ['ADMINISTRADOR', 'COORDINADOR', 'SUPERVISOR'].includes(r))
    const isAdmin = !!user?.roles?.includes('ADMINISTRADOR')
    const [tab, setTab] = useState<Tab>(canManage ? 'people' : 'mine')

    const tabs: { key: Tab; label: string }[] = [
        ...(canManage ? [{ key: 'people' as Tab, label: 'Personal' }, { key: 'expiring' as Tab, label: 'Por vencer' }] : []),
        { key: 'mine', label: 'Mis acreditaciones' },
        ...(isAdmin ? [{ key: 'types' as Tab, label: 'Tipos' }] : []),
    ]

    return (
        <div>
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-white">Acreditaciones</h1>
                <p className="text-sm text-gray-500">Licencias, cursos y certificaciones del personal, con su vencimiento.</p>
            </div>
            <div role="tablist" className="mb-5 flex flex-wrap gap-2">
                {tabs.map((t) => (
                    <button key={t.key} role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)}
                        className={`rounded-xl px-3 py-1.5 text-sm ${tab === t.key ? 'bg-blue-600 text-white' : 'text-gray-400 hover:bg-white/5'}`}>
                        {t.label}
                    </button>
                ))}
            </div>
            {tab === 'mine' && user && <PersonPanel userId={user.id} canManage={false} />}
            {tab === 'people' && <PeoplePanel />}
            {tab === 'expiring' && <ExpiringPanel />}
            {tab === 'types' && <TypesPanel />}
        </div>
    )
}

function LightBadge({ light }: { light: Light }) {
    return <span className={`rounded-full px-2 py-0.5 text-xs ${LIGHT_CLS[light]}`}>{LIGHT_LABEL[light]}</span>
}

function PeoplePanel() {
    const [people, setPeople] = useState<PersonView[]>([])
    const [selected, setSelected] = useState<string | null>(null)
    const [query, setQuery] = useState('')

    const load = useCallback(() => {
        credentialService.overview().then(setPeople).catch(() => toast.error('No se pudo cargar el personal'))
    }, [])
    useEffect(() => { load() }, [load])

    if (selected) {
        return (
            <div>
                <button onClick={() => { setSelected(null); load() }} className={`${btnGhost} mb-4`}>← Volver al personal</button>
                <PersonPanel userId={selected} canManage />
            </div>
        )
    }

    const q = query.trim().toLowerCase()
    const shown = q ? people.filter((p) => p.fullName.toLowerCase().includes(q) || p.username.toLowerCase().includes(q)) : people
    const count = (l: Light) => people.filter((p) => p.light === l).length

    return (
        <div>
            <div className="mb-4 flex flex-wrap items-center gap-3">
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar persona" aria-label="Buscar persona"
                    className={`${inputCls} max-w-xs`} />
                <span className="text-xs text-gray-500">
                    {count('RED')} pendientes · {count('AMBER')} por vencer · {count('GREEN')} al día
                </span>
            </div>
            <div className="overflow-x-auto rounded-2xl border border-white/10">
                <table className="w-full text-sm">
                    <thead className="text-left text-xs text-gray-500">
                        <tr>
                            <th className="px-3 py-2">Persona</th>
                            <th className="px-3 py-2">Estado</th>
                            <th className="px-3 py-2">Obligatorias vigentes</th>
                        </tr>
                    </thead>
                    <tbody>
                        {shown.map((p) => (
                            <tr key={p.userId} onClick={() => setSelected(p.userId)} className="cursor-pointer border-t border-white/5 hover:bg-white/[0.03]">
                                <td className="px-3 py-2 text-white">{p.fullName} <span className="text-xs text-gray-500">{p.username}</span></td>
                                <td className="px-3 py-2"><LightBadge light={p.light} /></td>
                                <td className="px-3 py-2 text-gray-300">{p.requiredValid} de {p.requiredTotal}</td>
                            </tr>
                        ))}
                        {shown.length === 0 && (
                            <tr><td colSpan={3} className="px-3 py-6 text-center text-gray-500">Sin personas</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

function PersonPanel({ userId, canManage }: { userId: string; canManage: boolean }) {
    const [person, setPerson] = useState<PersonView | null>(null)
    const [types, setTypes] = useState<CredentialType[]>([])
    const [form, setForm] = useState({ typeId: '', issuedAt: '', expiresAt: '', documentRef: '' })
    const [saving, setSaving] = useState(false)

    const load = useCallback(() => {
        credentialService.person(userId).then(setPerson).catch(() => toast.error('No se pudieron cargar las acreditaciones'))
    }, [userId])
    useEffect(() => { load() }, [load])
    useEffect(() => {
        if (canManage) credentialService.types().then((t) => setTypes(t.filter((x) => x.active))).catch(() => {})
    }, [canManage])

    const add = async (e: React.FormEvent) => {
        e.preventDefault()
        setSaving(true)
        try {
            await credentialService.add(userId, {
                typeId: Number(form.typeId), issuedAt: form.issuedAt,
                expiresAt: form.expiresAt || undefined, documentRef: form.documentRef || undefined,
            })
            toast.success('Acreditación registrada')
            setForm({ typeId: '', issuedAt: '', expiresAt: '', documentRef: '' })
            load()
        } catch (err) {
            toast.error(errorMessage(err, 'No se pudo registrar la acreditación'))
        } finally {
            setSaving(false)
        }
    }

    const remove = async (id: number) => {
        if (!window.confirm('¿Eliminar esta acreditación?')) return
        try {
            await credentialService.remove(userId, id)
            load()
        } catch (err) {
            toast.error(errorMessage(err, 'No se pudo eliminar'))
        }
    }

    if (!person) return <p className="text-sm text-gray-500">Cargando…</p>
    const selectedType = types.find((t) => String(t.id) === form.typeId)

    return (
        <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-3">
                <h2 className="text-lg font-semibold text-white">{person.fullName}</h2>
                <LightBadge light={person.light} />
                <span className="text-xs text-gray-500">{person.requiredValid} de {person.requiredTotal} obligatorias vigentes</span>
            </div>

            <div className="overflow-x-auto rounded-2xl border border-white/10">
                <table className="w-full text-sm">
                    <thead className="text-left text-xs text-gray-500">
                        <tr>
                            <th className="px-3 py-2">Acreditación</th>
                            <th className="px-3 py-2">Emitida</th>
                            <th className="px-3 py-2">Vence</th>
                            <th className="px-3 py-2">Estado</th>
                            <th className="px-3 py-2">Documento</th>
                            {canManage && <th className="px-3 py-2" />}
                        </tr>
                    </thead>
                    <tbody>
                        {person.credentials.map((c) => (
                            <tr key={c.typeId} className="border-t border-white/5">
                                <td className="px-3 py-2 text-white">
                                    {c.typeName} {c.required && <span className="text-[10px] uppercase text-gray-500">obligatoria</span>}
                                </td>
                                <td className="px-3 py-2 text-gray-300">{fmt(c.issuedAt)}</td>
                                <td className="px-3 py-2 text-gray-300">{fmt(c.expiresAt)}</td>
                                <td className={`px-3 py-2 ${STATE_CLS[c.state]}`}>
                                    {STATE_LABEL[c.state]}
                                    {c.daysLeft !== null && c.daysLeft >= 0 && <span className="ml-1 text-xs text-gray-500">({c.daysLeft} días)</span>}
                                </td>
                                <td className="px-3 py-2 text-gray-400">{c.documentRef ?? '—'}</td>
                                {canManage && (
                                    <td className="px-3 py-2 text-right">
                                        {c.id !== null && <button onClick={() => remove(c.id!)} className="text-xs text-rose-300 hover:underline">Eliminar</button>}
                                    </td>
                                )}
                            </tr>
                        ))}
                        {person.credentials.length === 0 && (
                            <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-500">Sin acreditaciones registradas</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {canManage && (
                <form onSubmit={add} className="grid grid-cols-1 gap-3 rounded-2xl border border-white/10 p-4 sm:grid-cols-4">
                    <h3 className="text-sm font-semibold text-white sm:col-span-4">Registrar o renovar</h3>
                    <label className="text-xs text-gray-400">Tipo
                        <select aria-label="Tipo" required value={form.typeId} onChange={(e) => setForm({ ...form, typeId: e.target.value })} className={`${inputCls} mt-1`}>
                            <option value="">Elegir…</option>
                            {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                    </label>
                    <label className="text-xs text-gray-400">Emitida
                        <input aria-label="Emitida" type="date" required value={form.issuedAt} onChange={(e) => setForm({ ...form, issuedAt: e.target.value })} className={`${inputCls} mt-1`} />
                    </label>
                    <label className="text-xs text-gray-400">Vence
                        <input aria-label="Vence" type="date" value={form.expiresAt} onChange={(e) => setForm({ ...form, expiresAt: e.target.value })} className={`${inputCls} mt-1`} />
                        <span className="mt-1 block text-[10px] text-gray-500">
                            {selectedType?.validityMonths ? `Vacío: ${selectedType.validityMonths} meses desde la emisión` : 'Vacío: no vence'}
                        </span>
                    </label>
                    <label className="text-xs text-gray-400">Documento (folio o número)
                        <input aria-label="Documento" value={form.documentRef} maxLength={200} onChange={(e) => setForm({ ...form, documentRef: e.target.value })} className={`${inputCls} mt-1`} />
                    </label>
                    <div className="sm:col-span-4">
                        <button type="submit" disabled={saving} className={btnPrimary}>Registrar</button>
                    </div>
                </form>
            )}
        </div>
    )
}

function ExpiringPanel() {
    const [days, setDays] = useState(30)
    const [items, setItems] = useState<ExpiringItem[]>([])

    useEffect(() => {
        credentialService.expiring(days).then(setItems).catch(() => toast.error('No se pudieron cargar los vencimientos'))
    }, [days])

    return (
        <div>
            <label className="mb-4 flex items-center gap-2 text-sm text-gray-400">
                Vencen en los próximos
                <select aria-label="Ventana" value={days} onChange={(e) => setDays(Number(e.target.value))}
                    className="rounded-xl border border-white/10 bg-white/[0.03] px-2 py-1 text-sm text-white">
                    {[15, 30, 60, 90].map((d) => <option key={d} value={d}>{d} días</option>)}
                </select>
            </label>
            <ul className="space-y-2">
                {items.map((i) => (
                    <li key={`${i.userId}-${i.typeName}`} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-white/10 px-3 py-2 text-sm">
                        <span className="text-white">{i.fullName} <span className="text-gray-400">· {i.typeName}</span></span>
                        <span className={i.daysLeft < 0 ? 'text-rose-300' : 'text-amber-300'}>
                            {i.daysLeft < 0 ? `vencida el ${fmt(i.expiresAt)}` : `vence el ${fmt(i.expiresAt)} (${i.daysLeft} días)`}
                        </span>
                    </li>
                ))}
                {items.length === 0 && <li className="text-sm text-gray-500">Nada vence en ese plazo.</li>}
            </ul>
        </div>
    )
}

const EMPTY_TYPE = { code: '', name: '', description: '', validityMonths: '', required: false }

function TypesPanel() {
    const [types, setTypes] = useState<CredentialType[]>([])
    const [form, setForm] = useState(EMPTY_TYPE)

    const load = useCallback(() => {
        credentialService.types().then(setTypes).catch(() => toast.error('No se pudieron cargar los tipos'))
    }, [])
    useEffect(() => { load() }, [load])

    const create = async (e: React.FormEvent) => {
        e.preventDefault()
        try {
            await credentialService.createType({
                code: form.code, name: form.name, description: form.description || undefined,
                validityMonths: form.validityMonths ? Number(form.validityMonths) : null, required: form.required, active: true,
            })
            toast.success('Tipo creado')
            setForm(EMPTY_TYPE)
            load()
        } catch (err) {
            toast.error(errorMessage(err, 'No se pudo crear el tipo'))
        }
    }

    const toggle = async (t: CredentialType, patch: Pick<TypeInput, 'required' | 'active'>) => {
        try {
            await credentialService.updateType(t.id, patch)
            load()
        } catch (err) {
            toast.error(errorMessage(err, 'No se pudo actualizar el tipo'))
        }
    }

    return (
        <div className="space-y-5">
            <div className="overflow-x-auto rounded-2xl border border-white/10">
                <table className="w-full text-sm">
                    <thead className="text-left text-xs text-gray-500">
                        <tr>
                            <th className="px-3 py-2">Tipo</th>
                            <th className="px-3 py-2">Vigencia</th>
                            <th className="px-3 py-2">Obligatoria</th>
                            <th className="px-3 py-2">Activo</th>
                        </tr>
                    </thead>
                    <tbody>
                        {types.map((t) => (
                            <tr key={t.id} className={`border-t border-white/5 ${t.active ? '' : 'opacity-50'}`}>
                                <td className="px-3 py-2 text-white">{t.name} <span className="font-mono text-xs text-gray-500">{t.code}</span></td>
                                <td className="px-3 py-2 text-gray-300">{t.validityMonths ? `${t.validityMonths} meses` : 'No vence'}</td>
                                <td className="px-3 py-2">
                                    <input type="checkbox" aria-label={`Obligatoria ${t.name}`} checked={t.required} onChange={(e) => toggle(t, { required: e.target.checked })} />
                                </td>
                                <td className="px-3 py-2">
                                    <input type="checkbox" aria-label={`Activo ${t.name}`} checked={t.active} onChange={(e) => toggle(t, { active: e.target.checked })} />
                                </td>
                            </tr>
                        ))}
                        {types.length === 0 && (
                            <tr><td colSpan={4} className="px-3 py-6 text-center text-gray-500">Aún no hay tipos de acreditación</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <form onSubmit={create} className="grid grid-cols-1 gap-3 rounded-2xl border border-white/10 p-4 sm:grid-cols-2">
                <h3 className="text-sm font-semibold text-white sm:col-span-2">Nuevo tipo</h3>
                <label className="text-xs text-gray-400">Código
                    <input aria-label="Código" required value={form.code} placeholder="LICENCIA_B"
                        onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className={`${inputCls} mt-1 font-mono`} />
                </label>
                <label className="text-xs text-gray-400">Nombre
                    <input aria-label="Nombre" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`${inputCls} mt-1`} />
                </label>
                <label className="text-xs text-gray-400">Vigencia en meses (vacío si no vence)
                    <input aria-label="Vigencia en meses" type="number" min={1} value={form.validityMonths}
                        onChange={(e) => setForm({ ...form, validityMonths: e.target.value })} className={`${inputCls} mt-1`} />
                </label>
                <label className="text-xs text-gray-400">Descripción
                    <input aria-label="Descripción" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputCls} mt-1`} />
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-300 sm:col-span-2">
                    <input type="checkbox" checked={form.required} onChange={(e) => setForm({ ...form, required: e.target.checked })} />
                    Obligatoria para todo el personal
                </label>
                <div className="sm:col-span-2"><button type="submit" className={btnPrimary}>Crear tipo</button></div>
            </form>
        </div>
    )
}
